import React from 'react';
import HorseCard from '../HorseCard/HorseCard';
import { AbandonedRace, isAbandonedRace, isPickedHorse, PickedHorse } from '../../store/entry/entryActions';
import { HorseInfo, InPlayProps, ResultProps, SelectionMade } from '../HorseCard/HorseCard.types';
import { Selection } from '../../interfaces/sports';
import { deepCopy } from '../../common/objectUtils';

interface GetCardsProps {
  pickedHorses: (PickedHorse | AbandonedRace | undefined)[];
  showUpdated: boolean;
  updateEventNumber?: number;
  onClick?: (eventNumber: number) => void;
  state: 'pre-play' | 'in-play';
  getHorseInfo: (selection: Selection) => HorseInfo;
}

export const getCards = ({
  pickedHorses,
  updateEventNumber,
  showUpdated,
  onClick,
  state,
  getHorseInfo,
}: GetCardsProps) => {
  let countdownShown = false;

  //figure out which is the next race to show
  const nextEventStart = deepCopy(pickedHorses)
    .filter(isPickedHorse)
    .sort((a, b) => (a?.raceStartTime ?? 0) - (b?.raceStartTime ?? 0))
    .find((selection) => selection && selection?.raceStartTime > Date.now())?.eventNumber;

  return pickedHorses
    .filter((ph) => ph?.active)
    .map((selection, i) => {
      if (!selection) return null;

      if (isAbandonedRace(selection))
        return (
          <HorseCard state={state === 'pre-play' ? 'selection-made' : 'resulted'} raceName={selection.eventName} />
        );

      const nonRunner = pickedHorses
        .filter(isPickedHorse)
        .find((ph) => ph?.eventNumber === selection.eventNumber && ph?.active === false);
      const horseInfo = getHorseInfo(selection);
      const props =
        state === 'pre-play'
          ? getPrePlayStateProps({
              showUpdated,
              selection,
              onClick,
              updateEventNumber,
              horseInfo,
            })
          : //only show countdown to race on the first horse card that has not started
            getInPlayStateProps({
              selection,
              showTime: !countdownShown && selection.eventNumber === nextEventStart,
              nonRunner: nonRunner ? getHorseInfo(nonRunner) : undefined,
              horseInfo,
            });
      if (!countdownShown && selection.eventNumber === nextEventStart) countdownShown = true;

      return <HorseCard key={i} {...props} />;
    });
};

interface SelectionMadeProps {
  selection: PickedHorse;
  showUpdated: boolean;
  updateEventNumber?: number;
  onClick?: (eventNumber: number) => void;
  horseInfo: HorseInfo;
}

const getPrePlayStateProps = ({
  showUpdated,
  selection,
  onClick,
  updateEventNumber,
  horseInfo,
}: SelectionMadeProps): SelectionMade => {
  const isUpdated = showUpdated && selection.eventNumber === updateEventNumber;
  horseInfo.nonRunner = !selection.selectionActive;
  return {
    state: 'selection-made',
    onClick: () => {
      //disable click while updated text is showing
      if (!showUpdated && onClick) onClick(selection.eventNumber);
    },
    updated: isUpdated,
    eventInfo: { name: selection.eventName },
    horseInfo,
  };
};

const getInPlayStateProps = ({
  selection,
  showTime,
  nonRunner,
  horseInfo,
}: {
  selection: PickedHorse;
  showTime: boolean;
  nonRunner?: HorseInfo;
  horseInfo: HorseInfo;
}): InPlayProps | ResultProps => {
  const isCountingDown = selection.raceStartTime > Date.now();
  if (isCountingDown) return getInPlayProps({ selection, showTime, horseInfo });
  return getResultedProps({ selection, nonRunner, horseInfo });
};

const getInPlayProps = ({
  selection,
  showTime,
  horseInfo,
}: {
  selection: PickedHorse;
  showTime: boolean;
  horseInfo: HorseInfo;
}): InPlayProps => {
  return {
    eventInfo: { name: selection.eventName },
    horseInfo,
    raceStartTime: showTime ? selection.raceStartTime : undefined,
    state: 'in-play',
    raceNumber: selection.eventNumber,
  };
};

export interface GetResultedProps {
  selection: PickedHorse;
  nonRunner?: HorseInfo;
  horseInfo: HorseInfo;
}

const getResultedProps = ({ selection, nonRunner, horseInfo }: GetResultedProps): ResultProps => {
  if (nonRunner) {
    return {
      eventInfo: { name: selection.eventName },
      horseInfo: nonRunner,
      position: 'NR',
      won: false,
      state: 'resulted',
      replaced: {
        horseInfo,
        position: selection.place || undefined,
        won: selection.selectionResult === 'W',
      },
    };
  }

  return {
    eventInfo: { name: selection.eventName },
    horseInfo,
    position: selection.place || undefined,
    won: selection.selectionResult === 'W',
    state: 'resulted',
  };
};
